import type { PointData, Container } from 'pixi.js';
import { CoinFlyParticle } from './CoinFlyParticle';
import { waitForMs } from '../../../utils/async-utils';

export interface CoinFlySequenceConfig {
  readonly startPoint: PointData;
  readonly targetPoint: PointData;
  readonly particleParent: Container;
  readonly coinCount: number;
  readonly maxIntervalMs?: number;
  readonly totalDurationCapMs?: number;
  readonly particleScale?: number;
  readonly unitsPerParticle?: number;
  readonly onCoinFlyStart?: (coinsFlown: number) => void;
  readonly onCoinArrived?: (coinsFlown: number) => void;
  readonly isCancelled?: () => boolean;
}

export async function animateCoinFlySequence(config: CoinFlySequenceConfig): Promise<void> {
  const {
    startPoint,
    targetPoint,
    particleParent,
    coinCount,
    maxIntervalMs = 100,
    totalDurationCapMs = 2000,
    particleScale = 0.5,
    unitsPerParticle = 1,
    onCoinFlyStart,
    onCoinArrived,
    isCancelled,
  } = config;

  if (coinCount <= 0) return;

  const step = Math.max(1, Math.floor(unitsPerParticle));
  const particleCount = Math.ceil(coinCount / step);

  const interval = Math.min(maxIntervalMs, totalDurationCapMs / Math.max(particleCount, 1));
  const promises: Promise<void>[] = [];

  let currentCoinCount = coinCount;
  for (let i = 0; i < particleCount; i += 1) {
    if (particleParent.destroyed || isCancelled?.()) break;
    const coinsFlown = Math.min(step, currentCoinCount);
    currentCoinCount -= coinsFlown;
    onCoinFlyStart?.(coinsFlown);
    promises.push(
      new CoinFlyParticle()
        .props({ parent: particleParent, scale: particleScale })
        .animate(startPoint, targetPoint)
        .then(
          () => {
            onCoinArrived?.(coinsFlown);
          },
          () => {},
        ),
    );
    await waitForMs(interval);
  }

  await Promise.all(promises);
}
